import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ChatConversation,
  ChatMessage,
  ToolActivity,
  TurnStatus,
} from "@/app/staff-management/ai-agent/types";

export interface AiChatState {
  /** Sidebar threads (active, most-recently-updated first). */
  conversations: ChatConversation[];
  /** `null` = a fresh, not-yet-created "New chat" draft. */
  activeConversationId: string | null;
  /** Messages keyed by conversation id (the active thread's history + live turn). */
  messagesByConversation: Record<string, ChatMessage[]>;
  /** Conversation ids whose history has been loaded (avoid refetch on re-select). */
  loadedConversationIds: string[];
  turnStatus: TurnStatus;
  toolActivity: ToolActivity | null;
  /** Last non-fatal turn error message (toast), cleared on the next send. */
  error: string | null;
  conversationsLoading: boolean;
  messagesLoading: boolean;
  /** FE-only pin state — the backend has no `pinned` field. */
  pinnedIds: string[];
}

const initialState: AiChatState = {
  conversations: [],
  activeConversationId: null,
  messagesByConversation: {},
  loadedConversationIds: [],
  turnStatus: "idle",
  toolActivity: null,
  error: null,
  conversationsLoading: false,
  messagesLoading: false,
  pinnedIds: [],
};

const messagesFor = (state: AiChatState, id: string): ChatMessage[] => {
  if (!state.messagesByConversation[id]) state.messagesByConversation[id] = [];
  return state.messagesByConversation[id];
};

const aiChatSlice = createSlice({
  name: "aiChat",
  initialState,
  reducers: {
    setConversations(state, action: PayloadAction<ChatConversation[]>) {
      state.conversations = action.payload;
    },
    setConversationsLoading(state, action: PayloadAction<boolean>) {
      state.conversationsLoading = action.payload;
    },
    /** Insert or update a single conversation, keeping it at the top. */
    upsertConversation(state, action: PayloadAction<ChatConversation>) {
      const next = action.payload;
      const rest = state.conversations.filter((c) => c.id !== next.id);
      state.conversations = [next, ...rest];
    },
    setActiveConversation(state, action: PayloadAction<string | null>) {
      state.activeConversationId = action.payload;
    },
    /** Start a brand-new draft thread (lazy-created server-side on first send). */
    startNewConversation(state) {
      state.activeConversationId = null;
      state.error = null;
      state.toolActivity = null;
    },
    setMessages(
      state,
      action: PayloadAction<{
        conversationId: string;
        messages: ChatMessage[];
      }>,
    ) {
      const { conversationId, messages } = action.payload;
      state.messagesByConversation[conversationId] = messages;
      if (!state.loadedConversationIds.includes(conversationId)) {
        state.loadedConversationIds.push(conversationId);
      }
    },
    setMessagesLoading(state, action: PayloadAction<boolean>) {
      state.messagesLoading = action.payload;
    },
    addUserMessage(
      state,
      action: PayloadAction<{ conversationId: string; message: ChatMessage }>,
    ) {
      messagesFor(state, action.payload.conversationId).push(
        action.payload.message,
      );
    },
    /** On `message_start`: append an empty assistant bubble keyed by message id. */
    startAssistantMessage(
      state,
      action: PayloadAction<{ conversationId: string; messageId: string }>,
    ) {
      const { conversationId, messageId } = action.payload;
      const list = messagesFor(state, conversationId);
      if (!list.some((m) => m.id === messageId)) {
        list.push({
          id: messageId,
          role: "assistant",
          content: "",
          status: "streaming",
        });
      }
    },
    /** On `text_delta`: append text to the live assistant bubble. */
    appendDelta(
      state,
      action: PayloadAction<{
        conversationId: string;
        messageId: string;
        text: string;
      }>,
    ) {
      const { conversationId, messageId, text } = action.payload;
      const msg = messagesFor(state, conversationId).find(
        (m) => m.id === messageId,
      );
      if (msg) msg.content += text;
    },
    /** On `message_complete`: replace deltas with the authoritative content. */
    completeAssistantMessage(
      state,
      action: PayloadAction<{
        conversationId: string;
        messageId: string;
        content: string;
        incomplete?: boolean;
      }>,
    ) {
      const { conversationId, messageId, content, incomplete } = action.payload;
      const msg = messagesFor(state, conversationId).find(
        (m) => m.id === messageId,
      );
      if (msg) {
        msg.content = content;
        msg.status = incomplete ? "incomplete" : "complete";
      }
    },
    /** Mark any still-streaming assistant bubble as complete (turn ended). */
    settleStreamingMessages(state, action: PayloadAction<string>) {
      for (const m of messagesFor(state, action.payload)) {
        if (m.status === "streaming") m.status = "complete";
      }
    },
    setTurnStatus(state, action: PayloadAction<TurnStatus>) {
      state.turnStatus = action.payload;
    },
    setToolActivity(state, action: PayloadAction<ToolActivity | null>) {
      state.toolActivity = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    renameConversationLocal(
      state,
      action: PayloadAction<{ id: string; title: string }>,
    ) {
      const conv = state.conversations.find((c) => c.id === action.payload.id);
      if (conv) conv.title = action.payload.title;
    },
    removeConversationLocal(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.conversations = state.conversations.filter((c) => c.id !== id);
      state.pinnedIds = state.pinnedIds.filter((p) => p !== id);
      delete state.messagesByConversation[id];
      state.loadedConversationIds = state.loadedConversationIds.filter(
        (l) => l !== id,
      );
      if (state.activeConversationId === id) state.activeConversationId = null;
    },
    togglePinLocal(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.pinnedIds = state.pinnedIds.includes(id)
        ? state.pinnedIds.filter((p) => p !== id)
        : [...state.pinnedIds, id];
    },
  },
});

export const {
  setConversations,
  setConversationsLoading,
  upsertConversation,
  setActiveConversation,
  startNewConversation,
  setMessages,
  setMessagesLoading,
  addUserMessage,
  startAssistantMessage,
  appendDelta,
  completeAssistantMessage,
  settleStreamingMessages,
  setTurnStatus,
  setToolActivity,
  setError,
  renameConversationLocal,
  removeConversationLocal,
  togglePinLocal,
} = aiChatSlice.actions;

export default aiChatSlice.reducer;
